import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText, Car, CheckCircle, Clock, Shield, Euro, Phone, 
  ExternalLink, MousePointerClick, ArrowRight, Truck, RefreshCw, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;

const services = [
  {
    id: 'immatriculation-neuve',
    title: 'Immatriculation Neuve',
    description: 'Première immatriculation de votre véhicule neuf',
    icon: Car,
    color: 'from-blue-500 to-blue-600'
  },
  {
    id: 'changement-proprietaire',
    title: 'Changement Propriétaire',
    description: 'Achat ou vente d\'un véhicule d\'occasion',
    icon: RefreshCw,
    color: 'from-emerald-500 to-emerald-600'
  },
  {
    id: 'changement-adresse',
    title: 'Changement d\'Adresse',
    description: 'Mise à jour de votre adresse',
    icon: MapPin,
    color: 'from-violet-500 to-violet-600'
  },
  {
    id: 'duplicata',
    title: 'Duplicata',
    description: 'Carte grise perdue ou volée',
    icon: FileText,
    color: 'from-amber-500 to-amber-600'
  }
];

export const CarteGrise = () => {
  const [formData, setFormData] = useState({
    serviceType: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    vehicleBrand: '',
    vehicleModel: '',
    currentRegistration: '',
    details: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const serviceLabels = {
        'immatriculation-neuve': 'Immatriculation véhicule neuf',
        'changement-proprietaire': 'Changement de propriétaire',
        'changement-adresse': 'Changement d\'adresse',
        'duplicata': 'Duplicata carte grise',
        'correction': 'Correction d\'erreur',
        'conversion': 'Conversion ancien format',
        'autre': 'Autre demande'
      };

      const contactData = {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        subject: `Carte Grise - ${serviceLabels[formData.serviceType] || formData.serviceType}`,
        message: `Type de démarche: ${serviceLabels[formData.serviceType] || formData.serviceType}

Informations véhicule:
- Marque: ${formData.vehicleBrand || 'Non renseigné'}
- Modèle: ${formData.vehicleModel || 'Non renseigné'}
- Immatriculation actuelle: ${formData.currentRegistration || 'Non renseigné'}

Détails supplémentaires:
${formData.details || 'Aucun'}`
      };

      await axios.post(`${BACKEND_URL}/api/contact`, contactData, {
        headers: { 'Content-Type': 'application/json' }
      });

      toast.success('Votre demande a été envoyée avec succès ! Nous vous contacterons sous 24h.');
      
      setFormData({
        serviceType: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        vehicleBrand: '',
        vehicleModel: '',
        currentRegistration: '',
        details: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Une erreur est survenue. Veuillez nous appeler au 05 82 81 91 20.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Service Carte Grise Moissac | CAMBC Conseils 82</title>
        <meta name="description" content="Service carte grise à Moissac : immatriculation, changement propriétaire, duplicata. Démarches simplifiées. Cabinet CAMBC Conseils." />
        <meta name="keywords" content="carte grise, immatriculation, changement propriétaire, duplicata carte grise, Moissac, service agréé" />
        <link rel="canonical" href="https://www.cambc-conseils.fr/carte-grise" />
      </Helmet>

      <div className="min-h-screen bg-[#F9F8F6]">
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 bg-[#0F2137] overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C5A059]/20 rounded-full text-[#C5A059] text-sm font-medium mb-6">
                <FileText size={16} />
                <span>Service agréé SIV</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                Démarches
                <span className="block text-[#C5A059]">Carte Grise</span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-8">
                Service complet pour toutes vos démarches d'immatriculation de véhicules.
                Rapide, simple et sans vous déplacer.
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-300">
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-[#C5A059]" />
                  <span>Service agréé</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={18} className="text-[#C5A059]" />
                  <span>Traitement 24-48h</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield size={18} className="text-[#C5A059]" />
                  <span>Accompagnement personnalisé</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C5A059] to-transparent"></div>
        </section>

        {/* Services Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {services.map((service) => (
                <button
                  key={service.id}
                  onClick={() => handleSelectChange('serviceType', service.id)}
                  className={`group p-5 sm:p-6 bg-white rounded-2xl border-2 transition-all duration-300 hover:shadow-lg text-left ${
                    formData.serviceType === service.id 
                      ? 'border-[#C5A059] shadow-lg' 
                      : 'border-slate-100 hover:border-[#C5A059]'
                  }`}
                  data-testid={`service-card-${service.id}`}
                >
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform`}>
                    <service.icon size={24} className="text-white" />
                  </div>
                  <h3 className="font-bold text-[#0F2137] text-sm sm:text-base mb-1">{service.title}</h3>
                  <p className="text-xs sm:text-sm text-slate-500">{service.description}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Info Banner */}
        <section className="py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-[#0F2137] rounded-2xl p-6 sm:p-8">
              <div className="grid sm:grid-cols-3 gap-6 text-center">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-[#C5A059]/20 flex items-center justify-center mb-3">
                    <Euro size={24} className="text-[#C5A059]" />
                  </div>
                  <h3 className="font-bold text-white text-lg">Tarifs Compétitifs</h3>
                  <p className="text-slate-400 text-sm">Devis gratuit et sans engagement</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-[#C5A059]/20 flex items-center justify-center mb-3">
                    <Clock size={24} className="text-[#C5A059]" />
                  </div>
                  <h3 className="font-bold text-white text-lg">Délais Courts</h3>
                  <p className="text-slate-400 text-sm">Traitement en 24 à 48h</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-[#C5A059]/20 flex items-center justify-center mb-3">
                    <Phone size={24} className="text-[#C5A059]" />
                  </div>
                  <h3 className="font-bold text-white text-lg">Accompagnement</h3>
                  <p className="text-slate-400 text-sm">Conseil et suivi personnalisé</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Self-Service Section - Certimat */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="bg-white border-0 shadow-lg rounded-2xl overflow-hidden border-l-4 border-l-emerald-500">
              <CardContent className="p-6 sm:p-8">
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <MousePointerClick className="w-6 h-6 text-emerald-600" />
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-[#0F2137]">Faites votre carte grise en ligne</h2>
                    </div>
                    <p className="text-slate-600 mb-4">
                      Vous préférez effectuer vos démarches vous-même ? Utilisez notre service en ligne partenaire pour réaliser votre carte grise en toute autonomie.
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle size={16} className="text-emerald-600" />
                        <span>Service agréé par le Ministère de l'Intérieur</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle size={16} className="text-emerald-600" />
                        <span>Paiement sécurisé</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle size={16} className="text-emerald-600" />
                        <span>Réception du certificat sous 24h</span>
                      </li>
                    </ul>
                  </div>
                  <div className="flex-shrink-0">
                    <a 
                      href="https://certimat.fr/iframe/prescripteurs?partner=5278" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      data-testid="certimat-link"
                    >
                      <Button 
                        size="lg" 
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 rounded-xl font-semibold"
                      >
                        <ExternalLink className="mr-2" size={20} />
                        Faire ma carte grise en ligne
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="bg-white border-0 shadow-xl rounded-2xl overflow-hidden">
              {/* Form Header */}
              <div className="bg-[#0F2137] p-6 sm:p-8">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[#C5A059]/20 flex items-center justify-center">
                    <FileText size={28} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Demande de Service Carte Grise</h2>
                    <p className="text-slate-400">Remplissez ce formulaire et nous vous recontacterons sous 24h</p>
                  </div>
                </div>
              </div>

              {/* Form Content */}
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={handleSubmit} className="space-y-8" data-testid="carte-grise-form">
                  {/* Service Type */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-6 bg-[#C5A059] rounded-full"></div>
                      <h3 className="text-lg font-bold text-[#0F2137]">Type de Démarche</h3>
                    </div>
                    <Select 
                      value={formData.serviceType} 
                      onValueChange={(value) => handleSelectChange('serviceType', value)} 
                      required
                    >
                      <SelectTrigger className="h-12 border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl" data-testid="service-type-select">
                        <SelectValue placeholder="Choisissez le type de démarche" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immatriculation-neuve">Immatriculation véhicule neuf</SelectItem>
                        <SelectItem value="changement-proprietaire">Changement de propriétaire</SelectItem>
                        <SelectItem value="changement-adresse">Changement d'adresse</SelectItem>
                        <SelectItem value="duplicata">Duplicata carte grise</SelectItem>
                        <SelectItem value="correction">Correction d'erreur</SelectItem>
                        <SelectItem value="conversion">Conversion ancien format</SelectItem>
                        <SelectItem value="autre">Autre demande</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-6 bg-emerald-500 rounded-full"></div>
                      <h3 className="text-lg font-bold text-[#0F2137]">Vos Coordonnées</h3>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#0F2137] font-medium">Prénom *</Label>
                        <Input
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="Votre prénom"
                          required
                          className="h-12 border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl"
                          data-testid="first-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#0F2137] font-medium">Nom *</Label>
                        <Input
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Votre nom"
                          required
                          className="h-12 border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl"
                          data-testid="last-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#0F2137] font-medium">Email *</Label>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="votre@email.fr"
                          required
                          className="h-12 border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl"
                          data-testid="email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#0F2137] font-medium">Téléphone *</Label>
                        <Input
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="06 12 34 56 78"
                          required
                          className="h-12 border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl"
                          data-testid="phone-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Vehicle Information */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-6 bg-violet-500 rounded-full"></div>
                      <h3 className="text-lg font-bold text-[#0F2137]">Informations Véhicule (optionnel)</h3>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#0F2137] font-medium">Marque</Label>
                        <Input
                          name="vehicleBrand"
                          value={formData.vehicleBrand}
                          onChange={handleChange}
                          placeholder="Ex: Renault"
                          className="h-12 border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl"
                          data-testid="vehicle-brand-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#0F2137] font-medium">Modèle</Label>
                        <Input
                          name="vehicleModel"
                          value={formData.vehicleModel}
                          onChange={handleChange}
                          placeholder="Ex: Clio"
                          className="h-12 border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl"
                          data-testid="vehicle-model-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#0F2137] font-medium">Immatriculation</Label>
                        <Input
                          name="currentRegistration"
                          value={formData.currentRegistration}
                          onChange={handleChange}
                          placeholder="AB-123-CD"
                          className="h-12 border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl"
                          data-testid="registration-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Details */}
                  <div className="space-y-2">
                    <Label className="text-[#0F2137] font-medium">Informations complémentaires (optionnel)</Label>
                    <Textarea
                      name="details"
                      value={formData.details}
                      onChange={handleChange}
                      placeholder="Précisez votre situation ou toute information utile..."
                      rows={4}
                      className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl resize-none"
                      data-testid="details-textarea"
                    />
                  </div>

                  {/* Info Box */}
                  <div className="p-4 bg-[#F5EFE6] rounded-xl">
                    <p className="text-sm text-[#0F2137]">
                      <strong>Documents à préparer :</strong> Selon votre démarche, vous pourrez avoir besoin de : carte grise actuelle, pièce d'identité, justificatif de domicile, certificat de cession, contrôle technique...
                    </p>
                  </div>

                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-[#C5A059] hover:bg-[#B08A45] text-white text-lg py-6 rounded-xl font-semibold"
                    disabled={isSubmitting}
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      'Envoi en cours...'
                    ) : (
                      <>
                        Envoyer ma demande
                        <ArrowRight size={20} className="ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#F5EFE6] flex items-center justify-center">
                  <Shield size={28} className="text-[#C5A059]" />
                </div>
                <h3 className="font-bold text-[#0F2137] mb-2">Service Agréé</h3>
                <p className="text-sm text-slate-600">Habilité par la Préfecture pour effectuer vos démarches</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#F5EFE6] flex items-center justify-center">
                  <Clock size={28} className="text-[#C5A059]" />
                </div>
                <h3 className="font-bold text-[#0F2137] mb-2">Gain de Temps</h3>
                <p className="text-sm text-slate-600">Plus besoin de vous déplacer en préfecture</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#F5EFE6] flex items-center justify-center">
                  <CheckCircle size={28} className="text-[#C5A059]" />
                </div>
                <h3 className="font-bold text-[#0F2137] mb-2">Simplicité</h3>
                <p className="text-sm text-slate-600">Nous gérons toutes les formalités pour vous</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
